import { computed, reactive, ref } from 'vue';
import { BREAKPOINTS, MAIN_BREAKPOINTS } from '@/setup/globals';


/**
 * Adds an viewport instance to Vue itself, which can be used by calling this.$viewport.
 */
const plugin = {
  install(app) {
    const viewport = ref(window.innerWidth);

    /**
     * Returns TRUE if viewport is smaller than XS.
     */
    const isXxs = computed(() => viewport.value < BREAKPOINTS.sm);

    /**
     * Returns TRUE if viewport is at least XS.
     */
    const isXs = computed(() => viewport.value >= BREAKPOINTS.xs);

    /**
     * Returns TRUE if viewport is at least SM.
     */
    const isSm = computed(() => viewport.value >= BREAKPOINTS.sm);

    /**
     * Returns TRUE if viewport is at least MD.
     */
    const isMd = computed(() => viewport.value >= BREAKPOINTS.md);

    /**
     * Returns TRUE if viewport is at least LG.
     */
    const isLg = computed(() => viewport.value >= BREAKPOINTS.lg);

    /**
     * Returns TRUE if viewport is at least XL.
     */
    const isXl = computed(() => viewport.value >= BREAKPOINTS.xl);

    /**
     * Checks if current viewport is mobile (<= md).
     */
    const isMobile = computed(() => !isSm.value);

    /**
     * Checks if current viewport is mobile (<= md).
     */
    const isTablet = computed(() => !isMd.value);

    /**
     * Returns the short name of the current viewport (e.g. 'md').
     */
    const currentViewport = computed(
      () =>
        Object.entries(BREAKPOINTS)
          ?.reverse()
          ?.find((breakpoint) => viewport.value >= breakpoint[1])?.[0] || '',
    );

    /**
     * Returns the short name of the current viewport (e.g. 'md').
     *
     * @returns {String}
     */
    const currentViewportSimple = computed(() => {
      const simpleBreakpoints = {
        xs: BREAKPOINTS.xs,
        sm: BREAKPOINTS.sm,
        md: BREAKPOINTS.md,
      };

      const foundBreakpoint = Object.entries(simpleBreakpoints)
        .reverse()
        .find((breakpoint) => viewport.value >= breakpoint[1]);

      // Default value for everything below xs.
      return foundBreakpoint ? foundBreakpoint[0] : MAIN_BREAKPOINTS.phone;
    });

    window.addEventListener('resizeend', () => {
      viewport.value = window.innerWidth;
    });

    app.config.globalProperties.$viewport = reactive({
      isXxs,
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isMobile,
      isTablet,
      currentViewport,
      currentViewportSimple,
    });
  },
};

export default plugin;
